import { useState } from 'react';

import {
  Box,
  Button,
  ButtonGroup,
  Combobox,
  Divider,
  FormField,
  Heading,
  Label,
  Modal,
  Paragraph,
  Tag,
  TextButton,
} from '@zillow/constellation-10';
import { IconCameraExposureFilled } from '@zillow/constellation-icons';

import { useAutoSpotlight } from './AutoSpotlightContext';

export function SpotlightSwapModal() {
  const ctx = useAutoSpotlight();
  const [targetAlias, setTargetAlias] = useState('');

  const open = Boolean(ctx?.swapCandidate);
  const occupants = ctx?.swapOccupants ?? [];

  if (!ctx) {
    return null;
  }

  // Default to the first occupant during render; targetAlias only holds the user's explicit pick,
  // and is reset on close so each time the dialog reopens it defaults fresh (no prop-sync effect).
  const selectedAlias = targetAlias || occupants[0]?.alias || '';

  const options = occupants.map((occupant) => ({
    label: occupant.label,
    meta: `${occupant.meta}${occupant.meta ? ' · ' : ''}${occupant.origin === 'pinned' ? 'Pinned' : 'Auto-optimized'}`,
    value: occupant.alias,
  }));

  const handleChange = (value: string | Array<string>) => {
    setTargetAlias(Array.isArray(value) ? (value[0] ?? '') : value);
  };

  const handleCancel = () => {
    setTargetAlias('');
    ctx.cancelSwap();
  };

  const handleConfirm = () => {
    setTargetAlias('');
    ctx.confirmSwap(selectedAlias);
  };

  return (
    <Modal.Root open={open} onOpenChange={(next) => !next && handleCancel()} size={{ base: 'fullScreen', md: 'md' }}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content data-panda-theme="zillow">
            <Modal.Header>
              <Heading level={2} textStyle="heading-sm">
                Spotlight is full
              </Heading>
            </Modal.Header>
            <Divider />
            <Modal.Body>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', alignItems: 'flex-start' }}>
                <Tag size="md" tone="yellow" icon={<IconCameraExposureFilled />}>
                  All {ctx.licenses} licenses in use
                </Tag>
                <Paragraph>
                  To pin <strong>{ctx.swapCandidateLabel}</strong> to Spotlight, move a license off one of your current
                  pinned Spotlight listings.
                </Paragraph>
                <FormField
                  label={<Label required>Listing to move off</Label>}
                  required
                  css={{ alignSelf: 'stretch' }}
                  control={
                    <Combobox
                      value={selectedAlias}
                      onChange={handleChange}
                      showLabelForValue
                      options={options}
                      placeholder="Choose a listing"
                      size="md"
                    />
                  }
                />
              </Box>
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <ButtonGroup aria-label="Actions">
                <TextButton tone="brand" onClick={handleCancel}>
                  Cancel
                </TextButton>
                <Button tone="brand" emphasis="filled" size="md" disabled={!selectedAlias} onClick={handleConfirm}>
                  Confirm pin
                </Button>
              </ButtonGroup>
            </Modal.Footer>
            <Modal.CloseButton />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}

export default SpotlightSwapModal;
